import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '@/stores/appStore';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { 
  UserCircle, 
  ClipboardCheck, 
  Wallet, 
  HeartHandshake,
  Eye,
  Calendar,
  CheckCircle2
} from 'lucide-react';
import { currencySymbols } from '@/types';
import { format } from 'date-fns';

interface MemberRecord {
  member: { id: string; full_name: string; email?: string; phone?: string; title?: string; join_date: string; status: string; dues_balance?: number; credit_balance?: number };
  attendance: {
    total_meetings: number;
    attended: number;
    missed: number;
    rate: number;
  };
  dues: {
    total_paid: number;
    outstanding: number;
    credit: number;
  };
  donations: {
    events_contributed: number;
    total_amount: number;
  };
}

export default function MemberPortal() {
  const { t } = useTranslation();
  const { user, currentOrganization } = useAppStore();
  
  const [record, setRecord] = useState<MemberRecord | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const currencySymbol = currentOrganization?.currency 
    ? currencySymbols[currentOrganization.currency as keyof typeof currencySymbols] || '$'
    : '$';

  useEffect(() => {
    fetchMemberRecord();
  }, [user, currentOrganization]);

  const fetchMemberRecord = async () => {
    if (!user || !currentOrganization) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      // Find member record by email
      const { data: memberData, error: memberError } = await supabase
        .from('members')
        .select('*')
        .eq('organization_id', currentOrganization.id)
        .eq('email', user.email)
        .maybeSingle();

      if (memberError && memberError.code !== 'PGRST116') throw memberError;

      if (!memberData) {
        setRecord(null);
        setIsLoading(false);
        return;
      }

      // Fetch attendance stats
      const { data: attendanceData } = await supabase
        .from('attendance')
        .select('is_present')
        .eq('member_id', memberData.id);

      const totalMeetings = attendanceData?.length || 0;
      const attended = attendanceData?.filter((a) => a.is_present).length || 0;

      // Fetch dues stats
      const { data: paymentsData } = await supabase
        .from('payments')
        .select('amount')
        .eq('member_id', memberData.id)
        .eq('payment_type', 'dues');

      const totalPaid = paymentsData?.reduce((sum, p) => sum + (p.amount || 0), 0) || 0;

      // Fetch donation stats
      const { data: donationsData } = await supabase
        .from('donations')
        .select('amount')
        .eq('member_id', memberData.id);

      const totalDonations = donationsData?.reduce((sum, d) => sum + (d.amount || 0), 0) || 0;

      setRecord({
        member: memberData,
        attendance: {
          total_meetings: totalMeetings,
          attended,
          missed: totalMeetings - attended,
          rate: totalMeetings > 0 ? Math.round((attended / totalMeetings) * 100) : 0,
        },
        dues: {
          total_paid: totalPaid,
          outstanding: memberData.dues_balance || 0,
          credit: memberData.credit_balance || 0,
        },
        donations: {
          events_contributed: donationsData?.length || 0,
          total_amount: totalDonations,
        },
      });
    } catch (error) {
      console.error('Error fetching member record:', error);
      toast.error(t('errors.generic'));
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Skeleton className="h-48" />
          <Skeleton className="h-48" />
          <Skeleton className="h-48" />
        </div>
      </div>
    );
  }

  if (!record) {
    return (
      <div className="text-center py-12">
        <UserCircle className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
        <h2 className="text-xl font-semibold mb-2">No Record Found</h2>
        <p className="text-muted-foreground max-w-md mx-auto">
          We couldn't find a member record matching your email ({user?.email}) in this organization. 
          Please contact your organization administrator.
        </p>
      </div>
    );
  }

  const { member, attendance, dues, donations } = record;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('memberPortal.title')}</h1>
          <p className="text-muted-foreground">{t('memberPortal.viewOnly')}</p>
        </div>
        <Badge variant="outline" className="flex items-center gap-1">
          <Eye className="w-3 h-3" />
          Read-only
        </Badge>
      </div>

      {/* Profile Card */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row items-start gap-6">
            <div className="w-24 h-24 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
              <UserCircle className="w-12 h-12 text-primary" />
            </div>
            <div className="flex-1">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-2xl font-bold">{member.full_name}</h2>
                  <p className="text-muted-foreground">{member.title}</p>
                </div>
                <Badge variant={member.status === 'active' ? 'default' : 'secondary'}>
                  {member.status === 'active' ? 'Active Member' : 'Inactive'}
                </Badge>
              </div>
              
              <div className="mt-4 grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Member Since</p>
                  <p className="font-medium">{format(new Date(member.join_date), 'MMMM dd, yyyy')}</p>
                </div>
                {member.phone && (
                  <div>
                    <p className="text-sm text-muted-foreground">Phone</p>
                    <p className="font-medium">{member.phone}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Attendance Card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-primary" />
              {t('memberPortal.attendance')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="text-center">
                <p className="text-4xl font-bold">{attendance.rate}%</p>
                <p className="text-sm text-muted-foreground">Attendance Rate</p>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <p className="text-lg font-semibold">{attendance.total_meetings}</p>
                  <p className="text-xs text-muted-foreground">Total</p>
                </div>
                <div>
                  <p className="text-lg font-semibold text-green-600">{attendance.attended}</p>
                  <p className="text-xs text-muted-foreground">Present</p>
                </div>
                <div>
                  <p className="text-lg font-semibold text-red-600">{attendance.missed}</p>
                  <p className="text-xs text-muted-foreground">Missed</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Dues Card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Wallet className="w-5 h-5 text-primary" />
              {t('memberPortal.dues')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">
                  {currencySymbol}{dues.total_paid.toLocaleString()}
                </p>
                <p className="text-sm text-muted-foreground">{t('memberPortal.totalPaid')}</p>
              </div>
              <div className="space-y-2">
                {dues.outstanding > 0 && (
                  <div className="flex items-center justify-between p-2 bg-red-50 rounded">
                    <span className="text-sm">Outstanding</span>
                    <span className="font-medium text-red-600">
                      {currencySymbol}{dues.outstanding.toLocaleString()}
                    </span>
                  </div>
                )}
                {dues.credit > 0 && (
                  <div className="flex items-center justify-between p-2 bg-green-50 rounded">
                    <span className="text-sm">Credit Balance</span>
                    <span className="font-medium text-green-600">
                      {currencySymbol}{dues.credit.toLocaleString()}
                    </span>
                  </div>
                )}
                {dues.outstanding === 0 && dues.credit === 0 && (
                  <div className="flex items-center justify-center p-4 bg-green-50 rounded">
                    <CheckCircle2 className="w-5 h-5 text-green-600 mr-2" />
                    <span className="text-green-600">All dues paid up to date</span>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Donations Card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <HeartHandshake className="w-5 h-5 text-primary" />
              {t('memberPortal.donations')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-600">
                  {currencySymbol}{donations.total_amount.toLocaleString()}
                </p>
                <p className="text-sm text-muted-foreground">{t('memberPortal.totalDonated')}</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-semibold">{donations.events_contributed}</p>
                <p className="text-sm text-muted-foreground">Events Contributed</p>
              </div>
              {donations.events_contributed === 0 && (
                <p className="text-center text-sm text-muted-foreground">
                  {t('memberPortal.noDonations')}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
          <CardDescription>Your recent transactions and attendance</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {/* Placeholder for recent activity - can be expanded with actual data */}
            <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="font-medium">Member since</p>
                  <p className="text-sm text-muted-foreground">
                    {format(new Date(member.join_date), 'MMMM dd, yyyy')}
                  </p>
                </div>
              </div>
            </div>
            
            {attendance.total_meetings > 0 && (
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div className="flex items-center gap-3">
                  <ClipboardCheck className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">Last Meeting Attendance</p>
                    <p className="text-sm text-muted-foreground">
                      {attendance.attended} out of {attendance.total_meetings} meetings attended
                    </p>
                  </div>
                </div>
              </div>
            )}

            {dues.total_paid > 0 && (
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Wallet className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="font-medium">Total Dues Paid</p>
                    <p className="text-sm text-green-600">
                      {currencySymbol}{dues.total_paid.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
